import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Analytics } from "@vercel/analytics/react";
  // import TestAlgoliaSearch from './components/TestAlgoliaSearch';


// Contexts
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { CityProvider } from './contexts/CityContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserLocationProvider } from './contexts/UserLocationContext';

// Guards
import AuthGuard from './components/guards/AuthGuard';
import AdminGuard from './components/guards/AdminGuard';

// Components
import ScrollToTop from './components/common/ScrollToTop';
import LoadingSpinner from './components/common/LoadingSpinner';
import RouteTracker from './components/common/RouteTracker';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./components/pages/LandingPage/LandingPage'));
const ComingSoon = lazy(() => import('./components/pages/ComingSoon/ComingSoon'));
const Login = lazy(() => import('./components/pages/Login/Login'));
const SignUp = lazy(() => import('./components/pages/SignUp/SignUp'));
const PricingPage = lazy(() => import('./components/pages/PricingPage/PricingPage'));
const ContactUs = lazy(() => import('./components/pages/ContactUs/ContactUs'));
const BookingPage = lazy(() => import('./components/pages/BookingPage/BookingPage'));
const BecomeServiceProvider = lazy(() => import('./components/pages/BecomeServiceProvider/BecomeServiceProvider'));

// Region pages
const AfricaPage = lazy(() => import('./components/pages/regions/AfricaPage'));
const EuropePage = lazy(() => import('./components/pages/regions/EuropePage'));
const NorthAmericaPage = lazy(() => import('./components/pages/regions/NorthAmericaPage'));
const SouthAmericaPage = lazy(() => import('./components/pages/regions/SouthAmericaPage'));
const AustraliaPage = lazy(() => import('./components/pages/regions/AustraliaPage'));
const SouthAsiaPage = lazy(() => import('./components/pages/regions/SouthAsiaPage'));
const EastAsiaPage = lazy(() => import('./components/pages/regions/EastAsiaPage'));
const SoutheastAsiaPage = lazy(() => import('./components/pages/regions/SoutheastAsiaPage'));
const MiddleEastPage = lazy(() => import('./components/pages/regions/MiddleEastPage'));

// Category pages
const FoodServicesPage = lazy(() => import('./components/pages/categories/FoodServicesPage'));
const LocalBusinessesPage = lazy(() => import('./components/pages/categories/LocalBusinessesPage'));
const HairServicesPage = lazy(() => import('./components/pages/categories/HairServicesPage'));
const OtherServicesPage = lazy(() => import('./components/pages/categories/OtherServicesPage'));

// Protected pages
const ServiceDetailPage = lazy(() => import('./components/pages/ServiceDetailPage/ServiceDetailPage'));
const CartPage = lazy(() => import('./components/pages/CartPage/CartPage'));
const OrderPage = lazy(() => import('./components/pages/OrderPage/OrderPage'));
const ServiceSubmit = lazy(() => import('./components/pages/ServiceSubmit/ServiceSubmit'));
const UserProfile = lazy(() => import('./components/pages/UserProfile/UserProfile'));
const SubmissionSuccess = lazy(() => import('./components/pages/SubmissionSuccess/SubmissionSuccess'));
const ProviderDashboard = lazy(() => import('./components/pages/ProviderDashboard/ProviderDashboard'));
const ServiceAnalytics = lazy(() => import('./components/pages/ServiceAnalytics/ServiceAnalytics'));
const LayoutSelectionPage = lazy(() => import('./components/pages/LayoutSelectionPage/LayoutSelectionPage'));
const AdminDashboard = lazy(() => import('./components/pages/AdminDashboard/AdminDashboard'));
const PaymentSuccess = lazy(() => import('./components/pages/PaymentSuccess/PaymentSuccess'));

// Loading overlay component
const LoadingOverlay = () => {
  const { isLoading, loadingMessage } = useLoading();
  return isLoading ? <LoadingSpinner message={loadingMessage} /> : null;
};

// Footer component
const Footer = () => (
  <footer className="footer">
    <p>© 2024 Servillia. All Rights Reserved.</p>
    <div className="footer-links">
      <a href="/become-service-provider" target="_blank" rel="noopener noreferrer">Become a Service Provider</a>
      {/* <a href="/pricing" target="_blank" rel="noopener noreferrer">Pricing</a> */}
      <a href="/docs/Servillia_Terms_of_Service.pdf" target="_blank" rel="noopener noreferrer">Terms of Service</a>
      <a href="/docs/Servillia_Privacy_Policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      <a href="/contact" target="_blank">Contact Us</a>
    </div>
  </footer>
);

// App content component
const AppContent = () => {
  return (
    <Router>
      <RouteTracker />
      <ScrollToTop />
      <div className="App">
        <LoadingOverlay />
        <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
          <Routes>
          <Route path="/" element={<ComingSoon/>} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/region/africa" element={<AfricaPage />} />
          <Route path="/region/europe" element={<EuropePage />} />
          <Route path="/region/north-america" element={<NorthAmericaPage />} />
          <Route path="/region/south-america" element={<SouthAmericaPage />} />
          <Route path="/region/australia" element={<AustraliaPage />} />
          <Route path="/region/south-asia" element={<SouthAsiaPage />} />
          <Route path="/region/east-asia" element={<EastAsiaPage />} />
          <Route path="/region/southeast-asia" element={<SoutheastAsiaPage />} />
          <Route path="/region/middle-east" element={<MiddleEastPage />} />
          <Route path="/food-services/:continent" element={<FoodServicesPage />} />
          <Route path="/local-businesses/:continent" element={<LocalBusinessesPage />} />
          <Route path="/hair-services/:continent" element={<HairServicesPage />} />
          <Route path="/other-services/:continent" element={<OtherServicesPage />} />
          {/* Service routes - support both legacy IDs and new slugs */}
          <Route path="/service/:serviceSlug" element={<ServiceDetailPage />} />
          <Route path="/service/:serviceSlug/:itemSlug/order" element={<OrderPage />} />
          <Route path="/service/:serviceSlug/:itemSlug/booking" element={<BookingPage />} />
          <Route path="/order" element={<OrderPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path='/become-service-provider' element={<BecomeServiceProvider />} />
          <Route path='/service-submit' element={<ServiceSubmit />} />
          <Route path="/user-profile" element={<AuthGuard><UserProfile /></AuthGuard>} />
          <Route path="/submission-success" element={<AuthGuard><SubmissionSuccess /></AuthGuard>} />
          <Route path="/provider-dashboard/:providerId" element={<AuthGuard><ProviderDashboard /></AuthGuard>} />
          <Route path="/provider-dashboard/:providerId/service-analytics/:serviceId" element={<AuthGuard><ServiceAnalytics /></AuthGuard>} />
          <Route path='/pricing' element={<PricingPage />} />
          <Route path='/layout-selection/:serviceId' element={<LayoutSelectionPage />} />
          <Route path='/contact' element={<ContactUs />} />
          <Route path='/booking' element={<BookingPage />} />
          <Route path='/payment-success' element={<AuthGuard><PaymentSuccess /></AuthGuard>} />

          {/* Protect Admin Dashboard */}
          <Route
            path="/admin-dashboard"
            element={<AdminGuard><AdminDashboard /></AdminGuard>}
          />
          </Routes>
        </Suspense>
        <Analytics />
        <Footer />
      </div>
      {/* <TestAlgoliaSearch /> */}
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <UserLocationProvider>
        <LoadingProvider>
          <CityProvider>
            <AppContent />
          </CityProvider>
        </LoadingProvider>
      </UserLocationProvider>
    </AuthProvider>
  );
}

export default App;
